import React from 'react';
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer, Legend } from 'recharts';
import { EdaData, EdaInsight } from '../../types';

interface EdaInsightsPieChartProps {
  edaData: EdaData | null;
}

// Define severity colors
const SEVERITY_COLORS: Record<string, string> = {
  hoch: '#d9534f',   // Red
  mittel: '#f0ad4e', // Orange
  niedrig: '#5cb85c', // Green
  unbekannt: '#cccccc' // Fallback/Unknown color
};

// Define distinct colors for insight types
const TYPE_COLORS = [
    '#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c', 
    '#d0ed57', '#ffc658', '#ff7f0e', '#d0743c', '#a05d56'
];

// Interface for Pie data points, now including sample messages
interface PieDataPoint {
    name: string;         // Severity or Type name
    value: number;        // Count of insights
    color: string;
    // Details for tooltip
    typesInSeverity?: Record<string, number>; // For Severity segments
    severitiesInType?: Record<string, number>; // For Type segments
    sampleMessages?: string[]; // Store first few messages
}

const MAX_SAMPLE_MESSAGES = 2; // Max messages to show in tooltip

// Helper to transform insights into data for two pie charts with details and sample messages
const transformDataForPieCharts = (insights: EdaInsight[] | undefined): { severityData: PieDataPoint[], typeData: PieDataPoint[] } => {
  if (!insights) return { severityData: [], typeData: [] };

  // Adjusted group structure to hold messages
  const severityGroups: Record<string, { count: number, types: Record<string, number>, messages: string[] }> = {};
  const typeGroups: Record<string, { count: number, severities: Record<string, number>, messages: string[] }> = {};

  insights.forEach(insight => {
    const severity = insight.severity?.toLowerCase() || 'unbekannt';
    const type = insight.type || 'Allgemein';
    const message = insight.message;

    // Process Severity Group
    if (!severityGroups[severity]) {
        severityGroups[severity] = { count: 0, types: {}, messages: [] };
    }
    severityGroups[severity].count += 1;
    severityGroups[severity].types[type] = (severityGroups[severity].types[type] || 0) + 1;
    if (severityGroups[severity].messages.length < MAX_SAMPLE_MESSAGES) {
        severityGroups[severity].messages.push(message);
    }

    // Process Type Group
    if (!typeGroups[type]) {
        typeGroups[type] = { count: 0, severities: {}, messages: [] };
    }
    typeGroups[type].count += 1;
    typeGroups[type].severities[severity] = (typeGroups[type].severities[severity] || 0) + 1;
    if (typeGroups[type].messages.length < MAX_SAMPLE_MESSAGES) {
        typeGroups[type].messages.push(message);
    }
  });

  const severityData = Object.entries(severityGroups).map(([name, data]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize
    value: data.count,
    color: SEVERITY_COLORS[name] || SEVERITY_COLORS.unbekannt,
    typesInSeverity: data.types,
    sampleMessages: data.messages // Add sample messages
  }));

  // Assign colors to types cyclically
  let typeColorIndex = 0;
  const typeData = Object.entries(typeGroups).map(([name, data]) => ({
    name: name,
    value: data.count,
    color: TYPE_COLORS[typeColorIndex++ % TYPE_COLORS.length],
    severitiesInType: data.severities,
    sampleMessages: data.messages // Add sample messages
  }));

  // Sort data (optional)
  severityData.sort((a, b) => b.value - a.value);
  typeData.sort((a, b) => b.value - a.value);

  return { severityData, typeData };
};

// Custom Tooltip Component - Enhanced
const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const data: PieDataPoint = payload[0].payload;
        const segmentType = data.typesInSeverity ? 'Severity' : 'Type';

        return (
            <div className="custom-tooltip pie-tooltip">
                <p className="label"><strong>{data.name} ({segmentType})</strong></p>
                <p className="intro">Anzahl Insights: {data.value}</p>
                
                {/* Show breakdown */} 
                {data.typesInSeverity && (
                    <div className="breakdown">
                        <p><strong>Typen in dieser Severity:</strong></p>
                        <ul>
                            {Object.entries(data.typesInSeverity)
                                .sort(([, countA], [, countB]) => countB - countA) // Sort by count desc
                                .map(([typeName, count]) => (
                                <li key={typeName}>{typeName}: {count}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {data.severitiesInType && (
                     <div className="breakdown">
                        <p><strong>Severities für diesen Typ:</strong></p>
                         <ul>
                            {Object.entries(data.severitiesInType)
                                .sort(([, countA], [, countB]) => countB - countA)
                                .map(([severityName, count]) => (
                                 <li key={severityName}>{severityName.charAt(0).toUpperCase() + severityName.slice(1)}: {count}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Show sample messages */} 
                {data.sampleMessages && data.sampleMessages.length > 0 && (
                    <div className="sample-messages">
                        <p><strong>Beispiel-Insights:</strong></p>
                        <ul>
                            {data.sampleMessages.map((msg, index) => (
                                <li key={index}>{msg}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
    return null;
};


export const EdaInsightsPieChart: React.FC<EdaInsightsPieChartProps> = ({ edaData }) => {
    const { severityData, typeData } = transformDataForPieCharts(edaData?.eda_results.insights);

    if (!edaData || (!severityData.length && !typeData.length)) {
        return null; 
    }

    return (
        <div className="chart-container card pie-chart-container">
            <h3>Insights Verteilung (Severity / Typ)</h3>
            <ResponsiveContainer width="100%" height={400}>
                <PieChart margin={{ top: 0, right: 0, bottom: 60, left: 0 }}> 
                    {/* Inner Pie: Severity */}
                    <Pie
                        data={severityData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        labelLine={false} 
                        nameKey="name" // Important for Tooltip/Legend to pick up the name
                    >
                        {severityData.map((entry, index) => (
                            <Cell key={`cell-severity-${index}`} fill={entry.color} />
                        ))}
                    </Pie>

                    {/* Outer Pie: Type */}
                    <Pie
                        data={typeData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={90} 
                        outerRadius={120}
                        fill="#82ca9d"
                        paddingAngle={1}
                        nameKey="name" // Important for Tooltip/Legend
                    >
                         {typeData.map((entry, index) => (
                            <Cell key={`cell-type-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    
                    {/* Use the custom tooltip */}
                    <Tooltip content={<CustomPieTooltip />} />

                    <Legend 
                        layout="horizontal" 
                        verticalAlign="bottom" 
                        align="center" 
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}; 